import { Link } from 'react-router';
import { useStore } from '../store/useStore';
import { trpc } from '../trpc/client';
import { ShoppingCart, Trash2, Plus, Minus, Shield, ArrowRight, Package } from 'lucide-react';

export default function Cart() {
  const { cart, removeFromCart, currency, user } = useStore();
  const dealIds = cart.map((c) => c.dealId);
  const { data: dealsData } = trpc.deal.list.useQuery({ limit: 100 });
  const allDeals = dealsData?.deals || [];

  const cartItems = cart.map((item) => {
    const deal = allDeals.find((d) => d.id === item.dealId);
    return { ...item, deal };
  }).filter((item) => item.deal);

  const total = cartItems.reduce((sum, item) => sum + Number(item.deal?.buyerPrice || 0) * item.quantity, 0);

  if (cart.length === 0) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-[#f8fafc] flex items-center justify-center">
        <div className="text-center">
          <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-[#1e293b] mb-2">Your Cart is Empty</h2>
          <p className="text-[#64748b] mb-6">Browse deals and add items to your cart</p>
          <Link to="/deals" className="px-6 py-3 bg-[#c2410c] text-white rounded-xl hover:bg-[#9a3412] transition-colors text-sm font-semibold">
            Browse Deals
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#f8fafc]">
      <div className="max-w-[1000px] mx-auto px-4 lg:px-8">
        <h1 className="text-3xl font-semibold text-[#1e293b] mb-6 font-display">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <div key={item.dealId} className="bg-white rounded-2xl border border-gray-100 p-5 flex gap-4">
                <div className="w-24 h-24 rounded-xl overflow-hidden shrink-0">
                  <img src={item.deal?.images?.[0] || ''} alt={item.deal?.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs text-[#c2410c] font-medium mb-0.5">{item.deal?.category?.name}</p>
                      <Link to={`/deals/${item.deal?.slug}`} className="text-sm font-semibold text-[#1e293b] hover:text-[#c2410c] transition-colors line-clamp-1">
                        {item.deal?.name}
                      </Link>
                      <p className="text-xs text-[#64748b] mt-1">{item.deal?.supplier?.companyName}</p>
                    </div>
                    <button onClick={() => removeFromCart(item.dealId)} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                      <button className="px-2.5 py-1.5 hover:bg-gray-50 transition-colors">
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="px-3 py-1.5 text-sm font-semibold">{item.quantity}</span>
                      <button className="px-2.5 py-1.5 hover:bg-gray-50 transition-colors">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className="text-lg font-bold text-[#1e293b] font-display">
                      {currency === 'USD' ? '$' : '€'}{(Number(item.deal?.buyerPrice) * item.quantity).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-gray-100 p-6 sticky top-24">
              <h3 className="text-lg font-semibold text-[#1e293b] mb-4">Order Summary</h3>
              
              <div className="space-y-3 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-[#64748b]">Subtotal ({cartItems.length} items)</span>
                  <span className="font-medium text-[#1e293b]">{currency === 'USD' ? '$' : '€'}{total.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#64748b]">IPO Margin (20%)</span>
                  <span className="font-medium text-[#1e293b]">Included</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#64748b]">Escrow Fee</span>
                  <span className="font-medium text-emerald-600">Free</span>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4 mb-4">
                <div className="flex justify-between">
                  <span className="font-semibold text-[#1e293b]">Total</span>
                  <span className="text-xl font-bold text-[#1e293b] font-display">{currency === 'USD' ? '$' : '€'}{total.toLocaleString()}</span>
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 bg-emerald-50 rounded-xl mb-4">
                <Shield className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <p className="text-xs text-emerald-700">Your payment will be held in escrow until you confirm delivery.</p>
              </div>

              <button className="w-full py-3.5 bg-[#c2410c] text-white rounded-xl hover:bg-[#9a3412] transition-colors text-sm font-semibold flex items-center justify-center gap-2">
                Proceed to Checkout <ArrowRight className="w-4 h-4" />
              </button>

              <Link to="/deals" className="block w-full py-3 text-center text-sm text-[#64748b] hover:text-[#c2410c] transition-colors mt-2">
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
